import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import _debounce from "lodash/debounce";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { TextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { SuccessToast } from "../../components/toast/Toasts";
import { getAllInquiries } from "./services/inquiry.services";
import { setFilterInquiry } from "./inquirySlice";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import ExpandableText from "../../components/common/ExpandableText";

interface DateFilterChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const DateFilterChangeWatcher: React.FC<DateFilterChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const dispatch = useAppDispatch();
  const FromDate = formRenderProps.valueGetter("FromDate");
  const ToDate = formRenderProps.valueGetter("ToDate");
  const FilterInquiry: any = useAppSelector(
    (state) => state.inquiry.FilterInquiry
  );

  useEffect(() => {
    if ((FromDate && ToDate) || (!FromDate && !ToDate)) {
      const filter = {
        FromDate: FromDate ? moment(FromDate).format("YYYY-MM-DD") : "",
        ToDate: ToDate ? moment(ToDate).format("YYYY-MM-DD") : "",
      };
      const payload = {
        ...FilterInquiry,
        ...filter,
      };
      dispatch(setFilterInquiry(payload));
    }
  }, [FromDate, ToDate]);
  return null;
};

const Inquiry: React.FC = () => {
  const dispatch = useAppDispatch();

  const isExcelExportAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );

  const exportRef = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (exportRef.current !== null) {
      exportRef.current.save();
    }
  };

  const FilterInquiry: any = useAppSelector(
    (state) => state.inquiry.FilterInquiry
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
    };
  }, []);
  useEffect(() => {
    const payload = {
      ...FilterInquiry,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllInquiries(payload));
  }, [FilterInquiry?.FromDate, FilterInquiry?.ToDate]);

  const formatedDateFilter = {
    FromDate: FilterInquiry.FromDate
      ? moment(FilterInquiry?.FromDate, "YYYY-MM-DD").toDate()
      : "",
    ToDate: FilterInquiry.ToDate
      ? moment(FilterInquiry?.ToDate, "YYYY-MM-DD").toDate()
      : "",
  };

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Inquiry List
          </Typography.h4>
          <div style={{ display: "flex", gap: "20px", alignItems: "start" }}>
            <Form
              onSubmit={() => {}}
              initialValues={formatedDateFilter}
              render={(formRenderProps: FormRenderProps) => (
                <FormElement style={{ width: "100%", display: "flex" }}>
                  <Field
                    wrapperStyle={{
                      margin: "0 0 0 10px",
                      width: 150,
                      flex: "none",
                    }}
                    name="FromDate"
                    component={FormDatePicker}
                    format={"dd/MM/yyyy"}
                    placeholder="From Date"
                  />
                  <Field
                    wrapperStyle={{
                      margin: "0 0 0 10px",
                      width: 150,
                      flex: "none",
                    }}
                    name="ToDate"
                    component={FormDatePicker}
                    format={"dd/MM/yyyy"}
                    minDate={moment(
                      formRenderProps.valueGetter("FromDate")
                    ).toDate()}
                    placeholder="To Date"
                  />
                  <DateFilterChangeWatcher formRenderProps={formRenderProps} />
                </FormElement>
              )}
            />
            {isExcelExportAccess && (
              <Button
                title="Export Excel"
                themeColor={"primary"}
                type="button"
                onClick={excelExport}
              >
                Export to Excel
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <InquiryGridComponent exportRef={exportRef} />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

interface InquiryGridGridProps {
  exportRef: MutableRefObject<ExcelExport | null>;
}

const InquiryGridComponent: React.FC<InquiryGridGridProps> = ({
  exportRef,
}) => {
  // const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const InquiryList = useAppSelector((state) => state.inquiry.InquiryList);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const loading = useAppSelector((state) => state.inquiry.loading);
  const FilterInquiry: any = useAppSelector(
    (state) => state.inquiry.FilterInquiry
  );
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const gridRef = useRef<any>(null);

  useEffect(() => {
    const payload = {
      ...FilterInquiry,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllInquiries(payload));
  }, [currentPage, pageLimit]);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 11 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExcelExport
        data={filter ? filterBy(InquiryList, filter) : InquiryList}
        ref={exportRef}
        fileName={`Inquiries ${moment().format("DD-MM-YYYY HH-mm")}.xlsx`}
      >
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={filter ? filterBy(InquiryList, filter) : InquiryList}
          onFilterChange={handleFilterChange}
          skip={currentPage * pageLimit}
          take={pageLimit}
          total={InquiryList[0]?.TotalRecord || InquiryList?.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 50, 100, 300, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="CreatedOn"
            title="Date"
            width={120}
            cells={{
              // filterCell: CommonFilterCell,
              data: (props: any) => {
                return (
                  <td>
                    {props?.dataItem?.CreatedOn
                      ? moment(props?.dataItem?.CreatedOn, "DD-MM-YYYY").format(
                          "DD MMM YYYY"
                        )
                      : ""}
                  </td>
                );
              },
            }}
          />
          <Column
            field="Name"
            title="Name"
            width={160}
            cells={{ filterCell: CommonFilterCell }}
          />
          <Column
            field="MobileNo"
            title="Mobile No"
            width={120}
            cells={{ filterCell: CommonFilterCell }}
          />
          <Column
            field="EmailID"
            title="Email"
            width={300}
            cells={{
              filterCell: CommonFilterCell,
              data: (props) => (
                <td
                  onClick={() => {
                    if (props.dataItem.EmailID) {
                      navigator.clipboard.writeText(props.dataItem.EmailID);
                      SuccessToast("Email Copied!");
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {props.dataItem.EmailID || ""}
                </td>
              ),
            }}
          />
          <Column
            field="TourName"
            title="Tour"
            width={210}
            cells={{ filterCell: CommonFilterCell }}
          />
          <Column
            field="SectorName"
            title="Sector"
            width={120}
            cells={{ filterCell: CommonFilterCell }}
          />

          <Column
            field="Remarks"
            title="Remarks"
            width={300}
            cells={{
              data: (props) =>
                props.dataItem.Remarks ? (
                  <ExpandableText text={props.dataItem.Remarks} />
                ) : (
                  <td></td>
                ),
            }}
          />
        </KendoGrid>
      </ExcelExport>
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterInquiry: any = useAppSelector(
    (state) => state.inquiry.FilterInquiry
  );

  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        limit: +pageLimit,
        skip: 0 * +pageLimit,
      };

      dispatch(getAllInquiries(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterInquiry,
        [inputField]: inputValue,
      };
      dispatch(setFilterInquiry(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterInquiry, [props.field]: "" };
    dispatch(setFilterInquiry(updatedFilters));

    const payload = {
      ...updatedFilters,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllInquiries(payload));
  };

  return (
    <td>
      <div className="d-flex">
        <TextBox
          value={FilterInquiry[props.field] || ""}
          name={props.field || ""}
          onChange={onChange}
        />
        <Button
          svgIcon={filterClearIcon}
          title="Clear"
          disabled={!FilterInquiry[props.field]}
          onClick={onClearButtonClick}
          type="button"
          style={{
            marginLeft: "8px",
          }}
        ></Button>
      </div>
    </td>
  );
};

export default Inquiry;
